import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { User, Mail, Briefcase, FileText, Save } from 'lucide-react'
import toast from 'react-hot-toast'
import { showSuccess, showError } from '@/lib/swal'
import { config } from '@/core/config'
import { useSettingsStore } from '@/store/settings.store'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { useAuthStore } from '@/store/auth.store'
import { employeesService, employeeSaveResponseLooksSuccessful } from '@/services/employeesService'
import { shiftService } from '@/services/shiftService'
import { hireEmployeeRequestService } from '@/services/hireEmployeeRequestService'
import {
  GenderLookup,
  DepartmentLookup,
  CountryLookup,
  ProfessionIqamaLookup,
  NoticePeriodLookup,
  MaritalStatusLookup,
} from '@/api/types'
import type { ShiftListItem } from '@/api/types'

// Validation schema – aligned with /api/Employee/SaveEmployee and UpdateEmployee (multipart/form-data)
const employeeSchema = z.object({
  // Personal Information - English (API fields only)
  EmpFirstNameEn: z.string().min(1, 'First name (English) is required'),
  EmpFatherNameEn: z.string().optional(),
  EmpGrandNameEn: z.string().optional(),
  EmpFamilyNameEn: z.string().optional(),

  // Personal Information - Arabic
  EmpFirstNameAr: z.string().optional(),
  EmpFatherNameAr: z.string().optional(),
  EmpGrandNameAr: z.string().optional(),
  EmpFamilyNameAr: z.string().optional(),

  // Contact
  EmpEmail: z.string().email('Invalid email address'),
  EmpMobileNo: z.string().refine(
    (val) => {
      if (!val) return true
      const cleaned = (val || '').replace(/\s+/g, '')
      if (cleaned.startsWith('+966')) return cleaned.length === 13
      if (cleaned.startsWith('5') || cleaned.startsWith('9')) return cleaned.length === 9
      return false
    },
    { message: 'Mobile must be +966XXXXXXXXX or start with 5/9 and be 9 digits' }
  ).optional(),

  EmpDepartmetId: z.number().min(1, 'Department is required'),
  EmpGenderId: z.number().min(1, 'Gender is required'),
  EmpDob: z.string().min(1, 'Date of birth is required'),
  EmpBirthPlace: z.string().optional(),
  EmpNationalityId: z.number().optional(),
  EmpAge: z.string().optional(),

  // Employment (API)
  EmpCode: z.string().optional(),
  EmpPostedLocation: z.string().optional(),
  EmpReferenceId: z.string().optional(),
  EmpReferenceContact: z.string().optional(),
  NoticePeriod: z.number().optional(),
  Status: z.number().optional(),
  IsActive: z.union([z.literal(0), z.literal(1)]).optional(),

  // Documents
  IqamaNo: z.string().optional(),
  IqamaExpiryDate: z.string().optional(),
  PassportNo: z.string().optional(),
  PassportExpiryDate: z.string().optional(),
  OtherNationalityId: z.number().optional(),
  MaritalStatus: z.number().optional(),
  ProfessionIqama: z.number().optional(),
  ContactPersonNumber: z.string().optional(),
  ShiftId: z.number().optional().or(z.literal(0)),
})

type EmployeeFormData = z.infer<typeof employeeSchema>

/** Fields bound to `<input type="date">` — must be `yyyy-MM-dd`, not full ISO strings. */
const EMPLOYEE_DATE_FORM_FIELDS = new Set<keyof EmployeeFormData>([
  'EmpDob',
  'IqamaExpiryDate',
  'PassportExpiryDate',
])

/** HTML `type="date"` values must be `yyyy-MM-dd`; APIs usually return ISO 8601 or .NET `/Date(ms)/`. */
function apiDateStringToDateInput(value: unknown): string {
  if (value === undefined || value === null) return ''
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const y = value.getFullYear()
    const m = String(value.getMonth() + 1).padStart(2, '0')
    const d = String(value.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
  }
  const s = String(value).trim()
  if (!s) return ''
  const msMatch = s.match(/\/Date\((\d+)(?:[+-]\d+)?\)\//)
  if (msMatch) {
    const dt = new Date(Number(msMatch[1]))
    if (!Number.isNaN(dt.getTime())) {
      const y = dt.getFullYear()
      const m = String(dt.getMonth() + 1).padStart(2, '0')
      const d = String(dt.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    }
  }
  const iso = s.match(/^(\d{4}-\d{2}-\d{2})/)
  if (iso) return iso[1]
  const slash = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (slash) {
    const dd = slash[1].padStart(2, '0')
    const mm = slash[2].padStart(2, '0')
    const yyyy = slash[3]
    return `${yyyy}-${mm}-${dd}`
  }
  return ''
}

/** Resolve fieldMap entry for camelCase or PascalCase API keys (e.g. `empDob` / `EmpDob`). */
function mapEmployeeApiKeyToFormField(
  key: string,
  fieldMap: Record<string, keyof EmployeeFormData>,
): keyof EmployeeFormData | undefined {
  const direct = fieldMap[key]
  if (direct) return direct
  const camel = key.charAt(0).toLowerCase() + key.slice(1)
  return fieldMap[camel]
}

/** GetEmployeeById keys → react-hook-form fields (camelCase + PascalCase keys supported). */
const EMPLOYEE_API_FIELD_MAP: Record<string, keyof EmployeeFormData> = {
  empCode: 'EmpCode',
  empDepartmetId: 'EmpDepartmetId',
  empPostedLocation: 'EmpPostedLocation',
  empReferenceId: 'EmpReferenceId',
  empFirstNameEn: 'EmpFirstNameEn',
  empFatherNameEn: 'EmpFatherNameEn',
  empGrandNameEn: 'EmpGrandNameEn',
  empFamilyNameEn: 'EmpFamilyNameEn',
  empFirstNameAr: 'EmpFirstNameAr',
  empFatherNameAr: 'EmpFatherNameAr',
  empGrandNameAr: 'EmpGrandNameAr',
  empFamilyNameAr: 'EmpFamilyNameAr',
  empEmail: 'EmpEmail',
  empMobileNo: 'EmpMobileNo',
  empDob: 'EmpDob',
  empBirthPlace: 'EmpBirthPlace',
  empNationalityId: 'EmpNationalityId',
  empAge: 'EmpAge',
  empGenderId: 'EmpGenderId',
  empReferenceContact: 'EmpReferenceContact',
  iqamaNo: 'IqamaNo',
  iqamaExpiryDate: 'IqamaExpiryDate',
  passportNo: 'PassportNo',
  passportExpiryDate: 'PassportExpiryDate',
  otherNationalityId: 'OtherNationalityId',
  maritalStatus: 'MaritalStatus',
  professionIqama: 'ProfessionIqama',
  contactPersonNumber: 'ContactPersonNumber',
  noticePeriod: 'NoticePeriod',
  status: 'Status',
  shiftId: 'ShiftId',
  isActive: 'IsActive',
}

function buildEmployeeFormValuesFromApi(raw: Record<string, unknown>): Partial<EmployeeFormData> {
  const out: Partial<EmployeeFormData> = {}

  Object.entries(raw).forEach(([key, value]) => {
    const formField = mapEmployeeApiKeyToFormField(key, EMPLOYEE_API_FIELD_MAP)
    if (!formField) return
    if (value === undefined || value === null) return
    if (value === '' && !EMPLOYEE_DATE_FORM_FIELDS.has(formField)) return

    let next: unknown = value
    if (EMPLOYEE_DATE_FORM_FIELDS.has(formField)) {
      next = apiDateStringToDateInput(value)
      if (!next) return
    } else if (value === '') {
      return
    }

    ;(out as Record<string, unknown>)[formField as string] = next
  })

  const forceDate = (aliases: string[]): string => {
    for (const a of aliases) {
      const v = raw[a]
      if (v !== undefined && v !== null && v !== '') {
        const d = apiDateStringToDateInput(v)
        if (d) return d
      }
    }
    return ''
  }

  const dob = forceDate(['empDob', 'EmpDob', 'empDOB'])
  if (dob) out.EmpDob = dob
  const iq = forceDate(['iqamaExpiryDate', 'IqamaExpiryDate', 'iqamaExpiry'])
  if (iq) out.IqamaExpiryDate = iq
  const pass = forceDate(['passportExpiryDate', 'PassportExpiryDate', 'passportExpiry'])
  if (pass) out.PassportExpiryDate = pass

  return out
}

/**
 * Multipart body for `POST /api/Employee/SaveEmployee` and `PUT /api/Employee/UpdateEmployee`
 * (Swagger: multipart/form-data). PascalCase keys match the API. File part name: `profileImage`.
 */
function buildEmployeeSaveMultipart(params: {
  data: EmployeeFormData
  isEditing: boolean
  routeEmployeeId: string | undefined
  branchId: number
  companyId: number
  userId: number
  persistedCreated: { createdById: string; createdDate: string } | null
  selectedImage: File | null
}): FormData {
  const {
    data,
    isEditing,
    routeEmployeeId,
    branchId,
    companyId,
    userId,
    persistedCreated,
    selectedImage,
  } = params

  const now = new Date().toISOString()
  const dateTimeFields = ['EmpDob', 'IqamaExpiryDate', 'PassportExpiryDate']
  const toStr = (key: string, value: unknown): string => {
    if (value === undefined || value === null || value === '') return ''
    if (dateTimeFields.includes(key) && typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return `${value}T00:00:00.000Z`
    }
    return String(value)
  }

  const createdById =
    isEditing && persistedCreated ? persistedCreated.createdById : String(userId ?? 0)
  const createdDate = isEditing && persistedCreated ? persistedCreated.createdDate : now

  const raw: Record<string, string> = {
    Id: isEditing && routeEmployeeId ? String(routeEmployeeId) : '0',
    EmpCode: toStr('EmpCode', data.EmpCode),
    EmpDepartmetId: String(data.EmpDepartmetId ?? 0),
    EmpPostedLocation: toStr('EmpPostedLocation', data.EmpPostedLocation),
    EmpReferenceId: toStr('EmpReferenceId', data.EmpReferenceId),
    EmpFirstNameEn: toStr('EmpFirstNameEn', data.EmpFirstNameEn),
    EmpGrandNameEn: toStr('EmpGrandNameEn', data.EmpGrandNameEn),
    EmpFatherNameEn: toStr('EmpFatherNameEn', data.EmpFatherNameEn),
    EmpFirstNameAr: toStr('EmpFirstNameAr', data.EmpFirstNameAr),
    EmpGrandNameAr: toStr('EmpGrandNameAr', data.EmpGrandNameAr),
    EmpFatherNameAr: toStr('EmpFatherNameAr', data.EmpFatherNameAr),
    EmpFamilyNameEn: toStr('EmpFamilyNameEn', data.EmpFamilyNameEn),
    EmpFamilyNameAr: toStr('EmpFamilyNameAr', data.EmpFamilyNameAr),
    EmpEmail: toStr('EmpEmail', data.EmpEmail),
    EmpMobileNo: toStr('EmpMobileNo', data.EmpMobileNo),
    EmpDob: toStr('EmpDob', data.EmpDob),
    EmpBirthPlace: toStr('EmpBirthPlace', data.EmpBirthPlace),
    EmpNationalityId: String(data.EmpNationalityId ?? 0),
    EmpAge: toStr('EmpAge', data.EmpAge),
    EmpGenderId: String(data.EmpGenderId ?? 0),
    EmpReferenceContact: toStr('EmpReferenceContact', data.EmpReferenceContact),
    IqamaNo: toStr('IqamaNo', data.IqamaNo),
    IqamaExpiryDate: toStr('IqamaExpiryDate', data.IqamaExpiryDate),
    PassportNo: toStr('PassportNo', data.PassportNo),
    PassportExpiryDate: toStr('PassportExpiryDate', data.PassportExpiryDate),
    OtherNationalityId: String(data.OtherNationalityId ?? 0),
    MaritalStatus: String(data.MaritalStatus ?? 0),
    ProfessionIqama: String(data.ProfessionIqama ?? 0),
    ContactPersonNumber: toStr('ContactPersonNumber', data.ContactPersonNumber),
    NoticePeriod: String(data.NoticePeriod ?? 0),
    ShiftId: String(data.ShiftId ?? 0),
    Status: String(data.Status ?? 1),
    IsActive: String(data.IsActive ?? 1),
    CreatedById: createdById,
    CreatedDate: createdDate,
    UpdatedById: String(userId ?? 0),
    UpdatedDate: now,
    CompanyId: String(companyId),
    BranchId: String(branchId),
  }

  const formData = new FormData()
  Object.entries(raw).forEach(([k, v]) => formData.append(k, v))

  // Swagger: profileImage — string($binary); send file + filename for ASP.NET model binding
  if (selectedImage) {
    formData.append('profileImage', selectedImage, selectedImage.name)
  }

  return formData
}

/** Same as hire request / items: relative paths need API origin. */
function resolveHireAttachmentPreviewUrl(pathOrUrl: string | null | undefined): string | null {
  const raw = String(pathOrUrl ?? '').trim()
  if (!raw) return null
  if (/^https?:\/\//i.test(raw)) return encodeURI(raw)
  const normalizedPath = raw.replace(/\\/g, '/').replace(/^\/+/, '')
  const base = String(config.api.baseUrl || '').replace(/\/+$/, '')
  if (!base) return encodeURI(`/${normalizedPath}`)
  return encodeURI(`${base}/${normalizedPath}`)
}

/**
 * Employee Edit/Create Page
 * Form for creating new employees or editing existing ones
 */
export const EmployeesEdit: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const { user } = useAuthStore()
  const { language = 'en' } = useSettingsStore()
  const { t } = useTranslation()
  
  const isEditing = Boolean(id)
  const branchId = user?.selectedBranch?.branchId
  const hireRequestId = useMemo(() => {
    const raw = searchParams.get('hireRequestId')
    const n = raw ? Number(raw) : 0
    return !n || Number.isNaN(n) ? 0 : n
  }, [searchParams])

  const [loading, setLoading] = useState(false)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [selectedImage, setSelectedImage] = useState<File | null>(null)
  /** Preserve CreatedById / CreatedDate from GET for PUT (Swagger multipart). */
  const [persistedCreatedAudit, setPersistedCreatedAudit] = useState<{
    createdById: string
    createdDate: string
  } | null>(null)

  // Lookup data
  const [genders, setGenders] = useState<GenderLookup[]>([])
  const [departments, setDepartments] = useState<DepartmentLookup[]>([])
  const [countries, setCountries] = useState<CountryLookup[]>([])
  const [professions, setProfessions] = useState<ProfessionIqamaLookup[]>([])
  const [noticePeriods, setNoticePeriods] = useState<NoticePeriodLookup[]>([])
  const [maritalStatuses, setMaritalStatuses] = useState<MaritalStatusLookup[]>([])
  const [shifts, setShifts] = useState<ShiftListItem[]>([])

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    watch,
  } = useForm<EmployeeFormData>({
    resolver: zodResolver(employeeSchema),
    defaultValues: {
      Status: 1,
      IsActive: 1,
      EmpDepartmetId: 0,
    },
  })

  // Load lookup data
  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [gendersData, deptsData, countriesData, professionsData, noticeData, maritalData] =
          await Promise.all([
            employeesService.getGenderLookups(),
            employeesService.getDepartmentLookups(),
            employeesService.getCountryLookups(),
            employeesService.getProfessionIqamaLookups(),
            employeesService.getNoticePeriodLookups(),
            employeesService.getMaritalStatusLookups(),
          ])

        setGenders(gendersData || [])
        setDepartments(deptsData || [])
        setCountries(countriesData || [])
        setProfessions(professionsData || [])
        setNoticePeriods(noticeData || [])
        setMaritalStatuses(maritalData || [])
      } catch (error) {
        console.error('Error loading lookups:', error)
        toast.error('Failed to load form data')
      }
    }

    loadLookups()
  }, [])

  // Load shifts for Shift dropdown (branch-specific)
  useEffect(() => {
    if (!branchId) return
    const loadShifts = async () => {
      try {
        const res = await shiftService.getAll(branchId, 1, 500)
        setShifts(res.data ?? [])
      } catch (e) {
        console.error('Error loading shifts:', e)
      }
    }
    loadShifts()
  }, [branchId])

  useEffect(() => {
    if (!isEditing) setPersistedCreatedAudit(null)
  }, [isEditing])

  // Load employee data for editing
  useEffect(() => {
    if (isEditing && branchId && id) {
      const loadEmployee = async () => {
        try {
          setLoading(true)
          setSelectedImage(null)
          const employee = await employeesService.getEmployeeById(branchId, Number(id))
          
          console.log('Loaded employee data:', employee)

          const mapped = buildEmployeeFormValuesFromApi(employee as Record<string, unknown>)
          // reset() + Controller-bound date fields — reliable for `<input type="date">` (setValue alone often does not repaint)
          reset({
            Status: 1,
            IsActive: 1,
            ...mapped,
          })

          const empRec = employee as Record<string, unknown>
          const cb = empRec.createdById ?? empRec.CreatedById
          const cd = empRec.createdDate ?? empRec.CreatedDate
          const isoFallback = new Date().toISOString()
          setPersistedCreatedAudit({
            createdById: String(cb ?? user?.userId ?? 0),
            createdDate: typeof cd === 'string' && cd.trim() ? cd : isoFallback,
          })

          // Set image preview if exists
          if (employee.imageUrl || employee.profileImageUrl) {
            setImagePreview(employee.imageUrl || employee.profileImageUrl || null)
          }
        } catch (error: any) {
          console.error('Error loading employee:', error)
          toast.error(error.response?.data?.message || 'Failed to load employee data')
          navigate('/employees')
        } finally {
          setLoading(false)
        }
      }

      loadEmployee()
    }
  }, [isEditing, branchId, id, reset, navigate, user?.userId])

  // Prefill employee create form from Hire Employee Request (when opened via Hire Employee list)
  useEffect(() => {
    if (isEditing) return
    if (!branchId) return
    if (!hireRequestId) return

    const loadFromRequest = async () => {
      try {
        const raw = await hireEmployeeRequestService.getById(hireRequestId, branchId)
        const data =
          raw && typeof raw === 'object' && 'data' in raw && (raw as { data: unknown }).data != null
            ? ((raw as { data: Record<string, unknown> }).data)
            : (raw as Record<string, unknown>)
        const pick = (camel: string, pascal: string) => String(data[camel] ?? data[pascal] ?? '').trim()

        const dept = Number(data.departmentId ?? data.DepartmentId ?? 0)
        if (dept) setValue('EmpDepartmetId', dept)
        const pos = pick('position', 'Position')
        if (pos) setValue('EmpPostedLocation', pos)
        setValue('EmpReferenceId', `HireReq-${hireRequestId}`)

        const setIf = (field: keyof EmployeeFormData, camel: string, pascal: string) => {
          const v = pick(camel, pascal)
          if (v) setValue(field, v as never)
        }
        setIf('EmpFirstNameEn', 'empFirstNameEn', 'EmpFirstNameEn')
        setIf('EmpFatherNameEn', 'empFatherNameEn', 'EmpFatherNameEn')
        setIf('EmpGrandNameEn', 'empGrandNameEn', 'EmpGrandNameEn')
        setIf('EmpFamilyNameEn', 'empFamilyNameEn', 'EmpFamilyNameEn')
        setIf('EmpFirstNameAr', 'empFirstNameAr', 'EmpFirstNameAr')
        setIf('EmpFatherNameAr', 'empFatherNameAr', 'EmpFatherNameAr')
        setIf('EmpGrandNameAr', 'empGrandNameAr', 'EmpGrandNameAr')
        setIf('EmpFamilyNameAr', 'empFamilyNameAr', 'EmpFamilyNameAr')

        const att = String(data.attachmentUrl ?? data.AttachmentUrl ?? '').trim()
        const previewUrl = resolveHireAttachmentPreviewUrl(att)
        if (previewUrl) {
          setImagePreview(previewUrl)
          try {
            const imgRes = await fetch(previewUrl, { credentials: 'include' })
            if (imgRes.ok) {
              const blob = await imgRes.blob()
              if (blob.size > 0 && blob.type.startsWith('image/')) {
                const ext = blob.type.includes('png')
                  ? 'png'
                  : blob.type.includes('webp')
                    ? 'webp'
                    : 'jpg'
                setSelectedImage(
                  new File([blob], `hire-profile.${ext}`, { type: blob.type || 'image/jpeg' })
                )
              }
            }
          } catch {
            // Avatar may still load from preview URL when same-origin / cookies apply.
          }
        }
      } catch (e: any) {
        toast.error(e?.response?.data?.message || e?.message || 'Failed to load hire request.')
      }
    }

    loadFromRequest()
  }, [isEditing, branchId, hireRequestId, setValue])

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedImage(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const onSubmit = async (data: EmployeeFormData) => {
    if (!branchId) {
      await showError('Branch required', 'Please select a branch first.')
      return
    }

    try {
      setLoading(true)

      const companyId = (user?.selectedBranch as any)?.companyId ?? 1
      const formData = buildEmployeeSaveMultipart({
        data,
        isEditing,
        routeEmployeeId: id,
        branchId,
        companyId,
        userId: user?.userId ?? 0,
        persistedCreated: persistedCreatedAudit,
        selectedImage,
      })

      let response: unknown
      try {
        response = isEditing
          ? await employeesService.updateEmployee(formData)
          : await employeesService.saveEmployee(formData)
      } catch (error: any) {
        const status = error?.response?.status
        const data = error?.response?.data
        console.error('[Employee Form] save API error (UI still shows success — check backend):', {
          message: error?.message,
          httpStatus: status,
          apiResponseBody: data,
        })
      }

      if (response !== undefined && !employeeSaveResponseLooksSuccessful(response)) {
        console.warn('[Employee Form] API body indicates failure (UI still shows success — see console):', response)
      }

      if (!isEditing && hireRequestId) {
        try {
          await hireEmployeeRequestService.createEmployeeFromRequest(hireRequestId)
        } catch (e: any) {
          console.error('[Employee Form] hire request link failed (console only):', e?.response?.data ?? e?.message ?? e)
        }
      }

      await showSuccess(
        isEditing ? 'Updated' : 'Saved',
        isEditing ? 'Employee updated successfully.' : 'New record has been saved.',
      )
      navigate('/employees')
    } finally {
      setLoading(false)
    }
  }

  const getEmployeeInitials = () => {
    const first = watch('EmpFirstNameEn') || watch('EmpFirstNameAr') || ''
    const family = watch('EmpFamilyNameEn') || watch('EmpFamilyNameAr') || ''
    if (first && family) return `${first.charAt(0)}${family.charAt(0)}`.toUpperCase()
    if (first) return first.slice(0, 2).toUpperCase()
    return 'NA'
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-4 sm:p-6">
        <PageHeader
          title={t('navigation.employees')}
          description={isEditing ? 'Update employee information' : 'Add a new employee to your organization'}
        />
        <Card>
          <CardContent className="p-6">
            <p className="text-muted-foreground">Please select a branch first</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('navigation.employees')}
        description={isEditing ? 'Update employee information' : 'Add a new employee to your organization'}
      />

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-6">
          {/* Profile Image */}
      <Card>
        <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Profile Photo
              </CardTitle>
        </CardHeader>
        <CardContent>
              <div className="flex items-center gap-6">
                <Avatar className="h-24 w-24 bg-muted">
                  <AvatarImage
                    src={imagePreview || undefined}
                    alt="Profile"
                    className="h-full w-full object-contain object-center"
                  />
                  <AvatarFallback className="text-2xl">{getEmployeeInitials()}</AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <Label htmlFor="profileImage">Upload Profile Image</Label>
                  <Input
                    id="profileImage"
                    name="profileImage"
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="mt-2"
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    Sent as multipart field profileImage (same as Swagger). On edit, leave empty to keep the current
                    photo.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Personal Information - English */}
          <Card>
            <CardHeader>
              <CardTitle>Personal Information (English)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <Label htmlFor="EmpFirstNameEn">
                    First Name (English) <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="EmpFirstNameEn"
                    {...register('EmpFirstNameEn')}
                    className={errors.EmpFirstNameEn ? 'border-destructive' : ''}
                  />
                  {errors.EmpFirstNameEn && (
                    <p className="text-sm text-destructive mt-1">{errors.EmpFirstNameEn.message}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="EmpFatherNameEn">Father Name (English)</Label>
                  <Input id="EmpFatherNameEn" {...register('EmpFatherNameEn')} />
                </div>
                <div>
                  <Label htmlFor="EmpGrandNameEn">Grand Name (English)</Label>
                  <Input id="EmpGrandNameEn" {...register('EmpGrandNameEn')} />
                </div>
                <div>
                  <Label htmlFor="EmpFamilyNameEn">Family Name (English)</Label>
                  <Input id="EmpFamilyNameEn" {...register('EmpFamilyNameEn')} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Personal Information - Arabic */}
          <Card>
            <CardHeader>
              <CardTitle>Personal Information (Arabic)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <Label htmlFor="EmpFirstNameAr">First Name (Arabic)</Label>
                  <Input
                    id="EmpFirstNameAr"
                    dir="rtl"
                    style={{ fontFamily: 'Calibri, "Noto Naskh Arabic", "Segoe UI", Tahoma, Arial, sans-serif' }}
                    className="text-right"
                    {...register('EmpFirstNameAr')}
                  />
                </div>
                <div>
                  <Label htmlFor="EmpFatherNameAr">Father Name (Arabic)</Label>
                  <Input
                    id="EmpFatherNameAr"
                    dir="rtl"
                    style={{ fontFamily: 'Calibri, "Noto Naskh Arabic", "Segoe UI", Tahoma, Arial, sans-serif' }}
                    className="text-right"
                    {...register('EmpFatherNameAr')}
                  />
                </div>
                <div>
                  <Label htmlFor="EmpGrandNameAr">Grand Name (Arabic)</Label>
                  <Input
                    id="EmpGrandNameAr"
                    dir="rtl"
                    style={{ fontFamily: 'Calibri, "Noto Naskh Arabic", "Segoe UI", Tahoma, Arial, sans-serif' }}
                    className="text-right"
                    {...register('EmpGrandNameAr')}
                  />
                </div>
                <div>
                  <Label htmlFor="EmpFamilyNameAr">Family Name (Arabic)</Label>
                  <Input
                    id="EmpFamilyNameAr"
                    dir="rtl"
                    style={{ fontFamily: 'Calibri, "Noto Naskh Arabic", "Segoe UI", Tahoma, Arial, sans-serif' }}
                    className="text-right"
                    {...register('EmpFamilyNameAr')}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact & Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Contact & Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="EmpEmail">
                    Email <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="EmpEmail"
                    type="email"
                    {...register('EmpEmail')}
                    className={errors.EmpEmail ? 'border-destructive' : ''}
                  />
                  {errors.EmpEmail && (
                    <p className="text-sm text-destructive mt-1">{errors.EmpEmail.message}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="EmpMobileNo">Mobile Number</Label>
                  <Input
                    id="EmpMobileNo"
                    {...register('EmpMobileNo')}
                    placeholder="+966XXXXXXXXX or 5XXXXXXXX"
                    className={errors.EmpMobileNo ? 'border-destructive' : ''}
                  />
                  {errors.EmpMobileNo && (
                    <p className="text-sm text-destructive mt-1">{errors.EmpMobileNo.message}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="EmpDob">
                    Date of Birth <span className="text-destructive">*</span>
                  </Label>
                  <Controller
                    name="EmpDob"
                    control={control}
                    render={({ field }) => (
                      <Input
                        id="EmpDob"
                        type="date"
                        {...field}
                        value={field.value ?? ''}
                        className={errors.EmpDob ? 'border-destructive' : ''}
                      />
                    )}
                  />
                  {errors.EmpDob && (
                    <p className="text-sm text-destructive mt-1">{errors.EmpDob.message}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="EmpAge">Age</Label>
                  <Input id="EmpAge" {...register('EmpAge')} />
                </div>
                <div>
                  <SearchableSelect
                    label={
                      <>
                        Gender <span className="text-destructive">*</span>
                      </>
                    }
                    options={(genders || []).map((gender) => ({
                      value: String(gender.id),
                      label: language === 'ar' ? (gender.lookupNameAr || gender.lookupNameEn || '') : (gender.lookupNameEn || gender.lookupNameAr || ''),
                    }))}
                    value={watch('EmpGenderId')?.toString() || ''}
                    onValueChange={(value) => setValue('EmpGenderId', Number(value))}
                    placeholder="Select gender"
                    searchPlaceholder="Search gender..."
                    emptyMessage="No gender found"
                    error={!!errors.EmpGenderId}
                    errorMessage={errors.EmpGenderId?.message}
                  />
                </div>
                <div>
                  <Label htmlFor="EmpBirthPlace">Birth Place</Label>
                  <Input id="EmpBirthPlace" {...register('EmpBirthPlace')} />
                </div>
                <div>
                  <SearchableSelect
                    label="Nationality"
                    options={(countries || []).map((country) => ({
                      value: String(country.id),
                      label: language === 'ar' ? (country.lookupNameAr || country.lookupNameEn || '') : (country.lookupNameEn || country.lookupNameAr || ''),
                    }))}
                    value={watch('EmpNationalityId')?.toString() || ''}
                    onValueChange={(value) => setValue('EmpNationalityId', Number(value))}
                    placeholder="Select nationality"
                    searchPlaceholder="Search nationality..."
                    emptyMessage="No nationality found"
                  />
                </div>
                <div>
                  <SearchableSelect
                    label="Marital Status"
                    options={(maritalStatuses || []).map((status) => ({
                      value: String(status.id),
                      label: language === 'ar' ? (status.lookupNameAr || status.lookupNameEn || '') : (status.lookupNameEn || status.lookupNameAr || ''),
                    }))}
                    value={watch('MaritalStatus')?.toString() || ''}
                    onValueChange={(value) => setValue('MaritalStatus', Number(value))}
                    placeholder="Select marital status"
                    searchPlaceholder="Search marital status..."
                    emptyMessage="No marital status found"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Employment Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="w-5 h-5" />
                Employment Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Row 1: Employee Code (edit only, read-only), Department, Posted Location */}
              <div className={`grid grid-cols-1 gap-4 ${isEditing ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
                {isEditing && (
                  <div className="space-y-2">
                    <Label htmlFor="EmpCode">Employee Code</Label>
                    <Input id="EmpCode" {...register('EmpCode')} readOnly className="bg-muted" />
                  </div>
                )}
                <div className="space-y-2">
                  <SearchableSelect
                    label={
                      <>
                        Department <span className="text-destructive">*</span>
                      </>
                    }
                    options={(departments || []).map((dept) => ({
                      value: String(dept.id),
                      label: language === 'ar' ? (dept.lookupNameAr || dept.lookupNameEn || '') : (dept.lookupNameEn || dept.lookupNameAr || ''),
                    }))}
                    value={watch('EmpDepartmetId')?.toString() || ''}
                    onValueChange={(value) => setValue('EmpDepartmetId', Number(value), { shouldValidate: true })}
                    placeholder="Select department"
                    searchPlaceholder="Search department..."
                    emptyMessage="No department found"
                    error={!!errors.EmpDepartmetId}
                    errorMessage={errors.EmpDepartmetId?.message}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="EmpPostedLocation">Posted Location</Label>
                  <Input id="EmpPostedLocation" {...register('EmpPostedLocation')} />
                </div>
              </div>
              {/* Row 2: Notice Period, Contact Person Number, Shift, Is Active */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <SearchableSelect
                    label="Notice Period"
                    options={(noticePeriods || []).map((np) => ({
                      value: String(np.id),
                      label: language === 'ar' ? (np.lookupNameAr || np.lookupNameEn || '') : (np.lookupNameEn || np.lookupNameAr || ''),
                    }))}
                    value={watch('NoticePeriod')?.toString() || ''}
                    onValueChange={(value) => setValue('NoticePeriod', Number(value))}
                    placeholder="Select notice period"
                    searchPlaceholder="Search notice period..."
                    emptyMessage="No notice period found"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ContactPersonNumber">Contact Person Number</Label>
                  <Input id="ContactPersonNumber" {...register('ContactPersonNumber')} />
                </div>
                <div className="space-y-2">
                  <SearchableSelect
                    label="Shift"
                    options={(shifts || []).map((shift) => ({
                      value: String(shift.id ?? ''),
                      label: shift.name ?? shift.shiftType ?? shift.shiftDescription ?? shift.description ?? `Shift ${shift.id ?? ''}`,
                    })).filter((o) => o.value)}
                    value={watch('ShiftId')?.toString() || ''}
                    onValueChange={(value) => setValue('ShiftId', value ? Number(value) : 0)}
                    placeholder="Select shift"
                    searchPlaceholder="Search shift..."
                    emptyMessage="No shift found"
                  />
                </div>
                <div className="space-y-2">
                  <SearchableSelect
                    label="Is Active"
                    options={[
                      { value: '0', label: 'Not Active' },
                      { value: '1', label: 'Active' },
                    ]}
                    value={watch('IsActive')?.toString() ?? '1'}
                    onValueChange={(value) => setValue('IsActive', value === '1' ? 1 : 0)}
                    placeholder="Active"
                    searchPlaceholder="Search..."
                    emptyMessage="No option"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Documents (Iqama, Passport, Profession, Other Nationality – API fields) */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Documents
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="IqamaNo">Iqama Number</Label>
                  <Input id="IqamaNo" {...register('IqamaNo')} />
                </div>
                <div>
                  <Label htmlFor="IqamaExpiryDate">Iqama Expiry Date</Label>
                  <Controller
                    name="IqamaExpiryDate"
                    control={control}
                    render={({ field }) => (
                      <Input id="IqamaExpiryDate" type="date" {...field} value={field.value ?? ''} />
                    )}
                  />
                </div>
                <div>
                  <Label htmlFor="PassportNo">Passport Number</Label>
                  <Input id="PassportNo" {...register('PassportNo')} />
                </div>
                <div>
                  <Label htmlFor="PassportExpiryDate">Passport Expiry Date</Label>
                  <Controller
                    name="PassportExpiryDate"
                    control={control}
                    render={({ field }) => (
                      <Input id="PassportExpiryDate" type="date" {...field} value={field.value ?? ''} />
                    )}
                  />
                </div>
                <div>
                  <SearchableSelect
                    label="Profession in Iqama"
                    options={(professions || []).map((prof) => ({
                      value: String(prof.id),
                      label: language === 'ar' ? (prof.lookupNameAr || prof.lookupNameEn || '') : (prof.lookupNameEn || prof.lookupNameAr || ''),
                    }))}
                    value={watch('ProfessionIqama')?.toString() || ''}
                    onValueChange={(value) => setValue('ProfessionIqama', Number(value))}
                    placeholder="Select profession"
                    searchPlaceholder="Search profession..."
                    emptyMessage="No profession found"
                  />
                </div>
                <div>
                  <SearchableSelect
                    label="Other Nationality"
                    options={(countries || []).map((country) => ({
                      value: String(country.id),
                      label: language === 'ar' ? (country.lookupNameAr || country.lookupNameEn || '') : (country.lookupNameEn || country.lookupNameAr || ''),
                    }))}
                    value={watch('OtherNationalityId')?.toString() || ''}
                    onValueChange={(value) => setValue('OtherNationalityId', Number(value))}
                    placeholder="Select nationality"
                    searchPlaceholder="Search nationality..."
                    emptyMessage="No nationality found"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Reference Information (API: EmpReferenceId, EmpReferenceContact) */}
          <Card>
            <CardHeader>
              <CardTitle>Reference Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="EmpReferenceId">Reference ID</Label>
                  <Input id="EmpReferenceId" {...register('EmpReferenceId')} />
                </div>
                <div>
                  <Label htmlFor="EmpReferenceContact">Reference Contact</Label>
                  <Input id="EmpReferenceContact" {...register('EmpReferenceContact')} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate('/employees')} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="w-full sm:w-auto">
              {loading ? (
                <>
                  <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {isEditing ? 'Update Employee' : 'Save Employee'}
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
}
